/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * 31 Milk St # 960789 Boston, MA 02196 USA.
 *
 * Copyright (c) 2020-2025 (original work) Open Assessment Technologies SA
 *
 */
import { Userpilot } from 'userpilot';
import env from '@/config/env';
import { refreshTenantConfiguration } from '@/core/utils/tenantConfigurationRefresh.js';

/**
 * Global router using the history API to dispatch the URL to mapped routes.
 *
 * Routes are defined in config.
 * Controllers must be written, as a string parameter of the import,
 * due to webpack's static parsing of import()
 *
 */
const router = {
    /**
     * Table which consists all the possible routes in our app.
     */
    routingTable: null,

    /**
     * Contains active controller instance
     */
    activeController: null,

    /**
     * Dispatch the router based on a URL.
     * The path will be used for the route and the parameters will be extracted.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/URL/URL
     * @param {string} url - the URL to dispatch
     * @param {string} [base] - the base URL
     * @throws {TypeError} if the URL is not correct (DOM requirements)
     * @returns {Promise}
     */
    dispatchURL(url, base) {
        try {
            const parsedUrl = new URL(url, base || window.location.href);
            const parameters = Object.fromEntries(parsedUrl.searchParams);
            const pathName = parsedUrl.pathname.replace(env('APP_NAMESPACE'), '');

            const result = this.dispatch(pathName, parameters);
            if (result) {
                Userpilot.reload();
            }
            return result;
        } catch (err) {
            throw new TypeError(`Invalid URL to dispatch :'${url}' (${err.message})`);
        }
    },

    /**
     * Dispatch the given route
     * @param {string} route - an internal route
     * @param {Object} parameters - parameters to be given to the controller
     * @returns {Promise}
     */
    dispatch(route, parameters = {}) {
        const controllerImport = this.routingTable(route);

        return controllerImport.then(({ default: controllerFactory }) => {
            if (typeof controllerFactory === 'function') {
                return this.startController(controllerFactory(), parameters);
            }
        });
    },

    /**
     * Prepare and start the provided controller
     * @param {PageController} controller
     * @param {Object} parameters - parameters to be given to the controller
     */
    async startController(controller, parameters = {}) {
        if (this.activeController) {
            this.stopActiveController();
        }

        this.activeController = controller;
        await refreshTenantConfiguration();
        controller.prepare();
        controller.mount();
        try {
            await controller.start(parameters);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    },

    /**
     * Stop controller and clean container
     */
    stopActiveController() {
        this.activeController.destroyAdditionalComponents();
        this.activeController.destroy();
        this.activeController.clean();
        this.activeController = null;
    },

    /**
     * Replace state
     * @param {object|string} state - History state object or URL
     * @returns {string}
     */
    replace(state) {
        if (typeof state === 'string') {
            state = {
                url: state
            };
        }

        window.history.replaceState(state, state.title, `${window.location.origin}${state.url}`);
        return this.dispatchURL(state.url);
    },

    /**
     * Push state
     * @param {object|string} state - History state object or URL
     * @returns {string}
     */
    redirect(state) {
        if (typeof state === 'string') {
            state = {
                url: state
            };
        }

        window.history.pushState(state, state.title, `${window.location.origin}${state.url}`);
        return this.dispatchURL(state.url);
    },

    /**
     * History pop state event handler
     */
    onPopState() {
        router.dispatchURL(window.location.href);
    },

    /**
     * Go to previous route within app (equivalent to browser's back)
     */
    goBack() {
        window.history.back();
    },

    /**
     * Entry point of the router that will start event listeners and check actual route
     * @param {Promise} routingTable - returns a controller
     * @returns {Promise}
     */
    start(routingTable) {
        this.routingTable = routingTable;

        //back & forward button, and push state
        window.addEventListener('popstate', this.onPopState);

        return this.dispatchURL(window.location.href);
    },

    /**
     * Stop router event listeners
     */
    stop() {
        window.removeEventListener('popstate', this.onPopState);
    }
};

export default router;
